import { type ComponentProps, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ChevronRightIcon } from 'components/icons';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth';
import { popupRowClass } from 'layout/MainLayout/Drawer/DrawerContent/Navigation/navStyles';
import { isMenuItemVisible } from 'menu-items/menuVisibility';
import type { NavItemType } from 'types/menu';
import { cn } from 'utils/cn';
import { FOCUS_RING } from '../../focusRing';
import MenuLink from './MenuLink';

type LinkProps = {
    link: NavItemType;
    active: boolean;
    className: string;
};

/** A section's link on one line: a title too long for it ends in an ellipsis, and shows in full on hover and focus. */
function SectionLink({ link, active, className }: LinkProps) {
    const label = useRef<HTMLSpanElement>(null);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const isCut = () => !!label.current && label.current.scrollWidth > label.current.clientWidth;

    return (
        <Tooltip
            content={link.title}
            side="right"
            open={tooltipOpen}
            onOpenChange={(open) => setTooltipOpen(open && isCut())}
        >
            <MenuLink item={link} aria-current={active ? 'page' : undefined} className={className}>
                <span ref={label} className="min-w-0 truncate">
                    {link.title}
                </span>
            </MenuLink>
        </Tooltip>
    );
}

type Props = {
    section: NavItemType;
    /** On the rail: the icon alone, its links in a popover beside it. */
    collapsed: boolean;
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    activeLinkId?: string;
};

/**
 * One collapsible menu section. Its header expands or collapses it. Its icon on the rail is a button that opens
 * the section's links in a popover: on hover, and on a click, Enter or Space, which also move focus to the first
 * link; Esc closes it and returns focus to the icon. While the popover is open the icon is drawn pressed.
 */
function NavSection({ section, collapsed, expanded, onExpandedChange, activeLinkId }: Props) {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const popupRef = useRef<HTMLDivElement>(null);
    const openedByHover = useRef(false);

    // A press on the icon opens the popover and moves focus into it, and so does a press that finds it already
    // open under the pointer: the kit would take that press, half a second into the hover, for the one that closes
    // it. Once focus is inside, the pointer leaving does not close it: that would leave focus on the page.
    const changePopover: ComponentProps<typeof Popover>['onOpenChange'] = (open, details) => {
        const popup = popupRef.current;
        if (open) {
            openedByHover.current = details.reason === 'trigger-hover';
        } else if (details.reason === 'trigger-press' && openedByHover.current) {
            openedByHover.current = false;
            details.cancel();
            popup?.querySelector('a')?.focus();
            return;
        } else if (details.reason === 'trigger-hover' && popup?.contains(document.activeElement)) {
            details.cancel();
            return;
        }
        setPopoverOpen(open);
    };

    // The rail's popover closes on navigation: opened by a click or a tap, it has no pointer leaving it to close
    // it, so it would stay over the page one of its links opened.
    const [prevPathname, setPrevPathname] = useState(pathname);
    if (pathname !== prevPathname) {
        setPrevPathname(pathname);
        setPopoverOpen(false);
    }

    const links = section.children?.filter((link) => isMenuItemVisible(link, user)) ?? [];
    const isActive = links.some((link) => link.id === activeLinkId);
    const Icon = section.icon;

    const linkList = (rowClassName: (active: boolean) => string, className?: string) => (
        <ul className={className}>
            {links.map((link) => (
                <li key={link.id}>
                    <SectionLink
                        link={link}
                        active={link.id === activeLinkId}
                        className={rowClassName(link.id === activeLinkId)}
                    />
                </li>
            ))}
        </ul>
    );

    if (collapsed) {
        return (
            <li>
                <Popover open={popoverOpen} onOpenChange={changePopover}>
                    <PopoverTrigger
                        openOnHover
                        delay={0}
                        className={cn(
                            'flex size-9 items-center justify-center rounded-md pointer-coarse:size-11',
                            FOCUS_RING,
                            isActive ? 'bg-sidebar-accent' : 'hover:bg-sidebar-hover data-popup-open:bg-sidebar-accent'
                        )}
                    >
                        {Icon && <Icon className="size-5" />}
                        <span className="sr-only">{section.title}</span>
                    </PopoverTrigger>
                    <PopoverContent
                        ref={popupRef}
                        side="right"
                        align="start"
                        sideOffset={10}
                        // Clears every overlay tier: the popover opens beside the rail even over an open side panel.
                        positionerClassName="z-(--z-blocking-loader)"
                        // `!`: `cn` does not know `shadow-dropdown` is a shadow, so it cannot drop the kit's `shadow-md`.
                        className="shadow-dropdown! w-auto max-w-72 min-w-[206px] gap-0 p-0 py-2 ring-0"
                    >
                        {/* Classic's row, without its own focus ring: `MenuLink` draws the new design's. */}
                        {linkList((active) => cn(popupRowClass(active), 'focus-visible:ring-0'))}
                    </PopoverContent>
                </Popover>
            </li>
        );
    }

    return (
        <li>
            <Collapsible open={expanded} onOpenChange={onExpandedChange}>
                <CollapsibleTrigger
                    className={cn(
                        'hover:bg-sidebar-hover relative flex h-8 w-full items-center gap-2.5 rounded-md ps-2.5 pe-2 text-start pointer-coarse:h-11 [&_svg[stroke]]:stroke-2',
                        // Closed, it covers the 1px to the next entry, as the entries do.
                        !expanded && 'after:absolute after:inset-x-0 after:top-full after:h-px',
                        FOCUS_RING,
                        isActive ? 'font-semibold' : 'font-medium'
                    )}
                >
                    {Icon && <Icon className="size-[18px]" />}
                    <span className="sidebar-fade min-w-0 flex-1 truncate">{section.title}</span>
                    <ChevronRightIcon
                        className={cn(
                            'sidebar-fade text-sidebar-muted size-3.5 shrink-0 motion-safe:transition-[rotate] motion-safe:duration-(--acc-chevron) motion-safe:ease-(--acc-ease) rtl:-scale-x-100',
                            expanded && 'rotate-90 rtl:-rotate-90'
                        )}
                    />
                </CollapsibleTrigger>
                <CollapsibleContent className="h-(--collapsible-panel-height) overflow-hidden transition-[height] duration-(--acc-expand) ease-(--acc-ease) data-ending-style:h-0 data-ending-style:duration-(--acc-collapse) data-starting-style:h-0 motion-reduce:transition-none">
                    {linkList(
                        (active) =>
                            cn(
                                // It covers the 1px to the next link, so the hover and a click there do not drop.
                                'relative flex h-7 items-center rounded-[7px] px-2.5 after:absolute after:inset-x-0 after:top-full after:h-px pointer-coarse:h-11',
                                active
                                    ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold'
                                    : 'hover:bg-sidebar-hover'
                            ),
                        'sidebar-fade border-sidebar-rule mb-1 ms-[19px] flex flex-col gap-px border-s ps-2.5'
                    )}
                </CollapsibleContent>
            </Collapsible>
        </li>
    );
}

export default NavSection;
